import { zodResolver } from "@hookform/resolvers/zod";
import {
  ReferenceField,
  FunctionField,
  useRecordContext,
} from "react-admin";
import { Alert } from "@mui/material";

import { JobMeta } from "./JobMeta";
import { joinAddressLine } from "../components/raFormat";
import {
  AppEdit,
  AppFormContent,
  AppFormSection,
  AppSimpleForm,
  AppTextInput,
  AppReferenceInput,
  SectionHeader,
  AppDateTimeInputDe,
  AppFormToolbar,
} from "../../layout";
import { jobEditFormSchema } from "../schemas";
import { toUpdateJobCommand } from "../mappers";

function JobStateHint() {
  const r = useRecordContext<any>();
  if (!r) return null;

  if (r.jobState === "COMPLETED") {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        Auftrag ist <b>abgeschlossen</b>. Änderungen sollten nur zur Korrektur
        erfolgen.
      </Alert>
    );
  }

  return null;
}

function JobEditFields() {
  return (
    <>
      <AppTextInput
        source="title"
        label="Titel"
        required
        fullWidth
        autoFocus
      />

      <AppReferenceInput
        source="constructionSiteId"
        reference="constructionSites"
        label="Einsatzort"
        autocompleteProps={{
          optionText: (record: any) => record.label || "—",
          fullWidth: true,
        }}
      />

      <AppDateTimeInputDe
        source="plannedStartAt"
        label="Geplanter Start"
        fullWidth
      />

      <AppReferenceInput
        source="assignedPersonId"
        reference="assignees"
        label="Zugewiesen"
        autocompleteProps={{
          optionText: "displayName",
          fullWidth: true,
        }}
      />
    </>
  );
}

function JobDescriptionFields() {
  return (
    <>
      <AppTextInput
        source="description"
        label="Beschreibung"
        multiline
        minRows={3}
        fullWidth
      />

      <AppTextInput
        source="notes"
        label="Notizen"
        multiline
        minRows={3}
        fullWidth
      />

      <ReferenceField
        source="constructionSiteId"
        reference="constructionSites"
        label="Adresse"
      >
        <ReferenceField
          source="addressId"
          reference="addresses"
          link="show"
          label=""
        >
          <FunctionField render={(address: any) => joinAddressLine(address)} />
        </ReferenceField>
      </ReferenceField>
    </>
  );
}

export const JobEdit = () => (
  <AppEdit
    mutationMode="pessimistic"
    aside={<JobMeta />}
    transform={toUpdateJobCommand}
  >
    <AppSimpleForm
      resolver={zodResolver(jobEditFormSchema) as any}
      mode="onBlur"
      toolbar={<AppFormToolbar />}
    >
      <JobStateHint />

      <AppFormSection>
        <SectionHeader
          title="Auftragsdaten"
          subtitle="Grunddaten und Zuordnung des Auftrags."
        />

        <AppFormContent>
          <JobEditFields />
        </AppFormContent>
      </AppFormSection>

      <AppFormSection>
        <SectionHeader
          title="Beschreibung"
          subtitle="Zusätzliche Informationen zum Auftrag."
        />

        <AppFormContent>
          <JobDescriptionFields />
        </AppFormContent>
      </AppFormSection>
    </AppSimpleForm>
  </AppEdit>
);
